import { useEffect, useState } from 'react'

import { useInView } from 'react-intersection-observer'
import { useInfiniteQuery } from 'react-query'

import { Page } from '@/components/layout/page/page'
import { LoadingOverlay } from '@/modules/shared/components/LoadingOverlay'
import { getVideos } from '@/modules/tiktok-research/service'
import { TikTokMediaGridSkeleton } from '@/modules/tiktok-research/skeletons/TikTokMediaGridSkeleton.tsx'
import { TikTokVideosFilters } from '@/modules/tiktok-research/videos/ui/filters/TikTokVideosFilters.tsx'
import type { TikTokVideosFiltersForm } from '@/modules/tiktok-research/videos/ui/filters/validation.ts'
import { defaultValues } from '@/modules/tiktok-research/videos/ui/filters/validation.ts'
import { VideoGrid } from '@/modules/tiktok-research/videos/ui/video-grid/VideoGrid.tsx'
import { TrackedFeatures, trackFeatureUsage } from '@/utils/feature-engagement'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy'

export const TikTokVideos = () => {
  const { ref, inView } = useInView()
  const [filters, setFilters] = useState<TikTokVideosFiltersForm>(defaultValues)

  const { data, fetchNextPage, isFetchingNextPage, hasNextPage, isFetching } =
    useInfiniteQuery(
      ['videos', JSON.stringify(filters)],
      ({ pageParam = 1, signal }) => getVideos(filters, pageParam, signal),
      {
        getNextPageParam: (page, pages) =>
          page.last ? undefined : pages.length + 1,
        refetchOnMount: true,
      },
    )

  useEffect(() => {
    if (data?.pages[0]?.content.length) {
      trackFeatureUsage(TrackedFeatures.TiktokResearch)
    }
  }, [data])

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage()
    }
  }, [inView, fetchNextPage, hasNextPage])

  const posts = data?.pages
    .flatMap((posts) => posts.content)
    .filter(isTruthyAndNotEmpty)

  return (
    <Page
      title="Videos"
      description="Find viral videos based on your niche"
      className="mb-8"
    >
      <TikTokVideosFilters
        className="mb-8"
        onChange={setFilters}
        value={filters}
        includePriority
      />
      {isFetching && !isFetchingNextPage ? (
        <>
          <LoadingOverlay message="Loading bulk video data..." />

          <TikTokMediaGridSkeleton />
        </>
      ) : (
        <VideoGrid
          videos={posts ?? []}
          lastElementRef={ref}
          isFetching={isFetching}
        />
      )}
    </Page>
  )
}
